import React, { forwardRef } from "react";
import { Popover } from "antd";
import roundedQuestion from "assets/svg/rounded-question.svg";
import { ErrorIcon } from "assets/icons";

type GInputProps = {
  label?: string;
  placeholder: string;
  setInput: (val: string) => void;
  inputValue?: string;
  enterClicked?: () => void;
  controlled?: boolean;
  shorter?: boolean;
  hintText?: string;
  required?: boolean;
  inputName?: string;
  error?: string; // Error message to display
  labelClassName?: string;
  inputBackgroundColor?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const GInput = forwardRef<HTMLInputElement, GInputProps>(
  (
    {
      label,
      placeholder,
      setInput,
      inputValue,
      enterClicked,
      controlled,
      shorter,
      hintText,
      required,
      error,
      className,
      onKeyDown,
      onChange,
      name,
      labelClassName,
      inputBackgroundColor,
      ...rest
    },
    ref,
  ) => {
    const isControlled = controlled || typeof inputValue === "string";

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
      onKeyDown?.(e);
      if (!e.defaultPrevented && e.key === "Enter" && enterClicked) {
        enterClicked();
      }
    };

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      onChange?.(e);
      setInput(e.target.value);
    };

    return (
      <div className={"my-3"}>
        {label && (
          <div className="flex items-center justify-between">
            <p
              className={
                labelClassName ??
                `text-[#344054] mb-1 font-monaMedium relative text-xs lg:text-sm`
              }
            >
              {label}
              {required && (
                <span className="flex absolute -right-2 text-[#ED6E33] -top-1 items-center text-xl">
                  *
                </span>
              )}
            </p>
            {hintText && (
              <Popover content={hintText}>
                <img className="cursor-pointer" src={roundedQuestion} alt="" />
              </Popover>
            )}
          </div>
        )}

        <input
          ref={ref}
          value={isControlled ? (inputValue ?? "") : undefined}
          onKeyDown={handleKeyDown}
          onChange={handleChange}
          style={{
            backgroundColor: inputBackgroundColor ?? "#F6F3EF",
          }}
          className={
            className ??
            `border ${
              error
                ? "border-[#F04438] hover:border-[#F04438]" // Red border on error
                : "border-transparent hover:border-[#D0D5DD]"
            } outline-none w-full lg:text-sm ${
              shorter ? "py-2 px-3" : "p-3"
            } font-sfRegular rounded-lg transition-colors duration-150 focus:outline-none focus:border-transparent focus:ring-2 focus:ring-[#FF6929]`
          }
          placeholder={placeholder}
          name={name}
          {...rest}
        />

        {/* Error message */}
        {error && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <ErrorIcon />
            <p className="text-[#F04438] text-xs lg:text-sm font-sfRegular">
              {error}
            </p>
          </div>
        )}
      </div>
    );
  },
);

GInput.displayName = "GInput";
